import { useWindowSize } from '../../../contexts/windowSizeContext';
import './heap-sort-visualizer.scss';

import React, { useState, useEffect, useCallback } from 'react';

const generateRandomArray = (size: number, maxHeight: number): number[] => {
  return Array.from({ length: size }, () => Math.floor(Math.random() * maxHeight) + 10);
};

/**
 * Numero di barre per fascia di viewport.
 *
 * Il vincolo viene dallo SCSS: ogni barra ha `margin: 2px` per lato, quindi 4px
 * fissi di overhead, e la larghezza utile e' `100% / N - 4px`. Non scalando N
 * con la larghezza quel valore diventa sub-pixel e poi negativo, e le barre
 * collassano sui soli bordi.
 *
 * Le soglie sono scelte controllando il LIMITE INFERIORE di ogni fascia, che e'
 * il punto peggiore: e' li' che N e' massimo rispetto allo spazio disponibile.
 *
 *   W=320  N=40  ->  4.00px      W=900   N=110 ->  4.18px
 *   W=500  N=60  ->  4.33px      W=1400  N=200 ->  3.00px
 *
 * Sopra i 1400px restano 200 barre, cioe' la densita' invariata su monitor.
 */
const barCountFor = (viewport: number): number => {
  if (viewport < 500) return 40;
  if (viewport < 900) return 60;
  if (viewport < 1400) return 110;
  return 200;
};

const HeapSortVisualizer: React.FC = () => {
  const { width } = useWindowSize();
  const arraysize = barCountFor(width);
  const maxheight = 250;
  const [bars, setBars] = useState<number[]>(generateRandomArray(arraysize, maxheight));
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [sorting, setSorting] = useState(false);

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const heapify = async (arr: number[], n: number, i: number) => {
    let largest = i;
    const left = 2 * i + 1;
    const right = 2 * i + 2;

    if (left < n && arr[left] > arr[largest]) largest = left;
    if (right < n && arr[right] > arr[largest]) largest = right;

    if (largest !== i) {
      [arr[i], arr[largest]] = [arr[largest], arr[i]];
      setBars([...arr]);
      setActiveIndex(i); // Evidenzia la barra attiva
      await sleep(500);
      await heapify(arr, n, largest);
    }
  };

  const heapSort = useCallback(async () => {
    setSorting(true);
    const arr = [...bars];
    const n = arr.length;

    // Costruzione max heap
    for (let i = Math.floor(n / 2) - 1; i >= 0; i--) {
      await heapify(arr, n, i);
    }

    // Estrazione elementi dall'heap
    for (let i = n - 1; i > 0; i--) {
      [arr[0], arr[i]] = [arr[i], arr[0]];
      setBars([...arr]);
      setActiveIndex(0); // Evidenzia la barra principale
      await sleep(500);
      await heapify(arr, i, 0);
    }

    setActiveIndex(null); // Reset evidenziazione
    setSorting(false);

    // Dopo 5 secondi, genera un nuovo array
    setTimeout(() => {
      setBars(generateRandomArray(arraysize, maxheight));
    heapSort();
    }, 5000);
  }, [bars]);
  // **Avvia l'ordinamento 4 secondi dopo il caricamento della pagina**
  useEffect(() => {
    const timer = setTimeout(() => {
      heapSort();
    }, 4000);

    return () => clearTimeout(timer);
  }, [heapSort]);

  return (
    // --array-size guida la larghezza di .bar nello SCSS. Vale bars.length e
    // non arraysize di proposito: i due numeri coincidono alla generazione, ma
    // su un resize che attraversa una soglia arraysize cambia subito mentre
    // bars conserva la lunghezza precedente fino al prossimo array generato, e
    // le barre sborderebbero dal contenitore. Legandolo a cio' che e' davvero
    // renderizzato la larghezza non puo' desincronizzarsi.
    <div
      className="bar-container"
      style={{ '--array-size': bars.length } as React.CSSProperties}
    >
    {bars.map((h, idx) => {
        const barClass = activeIndex === idx ? 'active' : sorting ? 'sorted' : '';
        return (
      <div key={`${h}-${idx}`} className={`bar ${barClass}`} data-height={h} style={{ height: `${h}px` }}></div>
        );
      })}
    </div>
  );
};

export default HeapSortVisualizer;
